/**
 * Affirmations and Wisdom from "The Power of Your Subconscious Mind"
 * by Joseph Murphy
 *
 * These affirmations are designed to reprogram the subconscious mind
 * for success, abundance, health, and positive thinking.
 */

export interface SubconsciousAffirmation {
  id: string;
  text: string;
  theme: 'wealth' | 'health' | 'success' | 'peace' | 'relationships' | 'sleep' | 'confidence';
  chapter?: string;
}

/**
 * Collection of affirmations organized by theme
 */
export const subconsciousAffirmations: SubconsciousAffirmation[] = [
  // Wealth & Abundance
  {
    id: 'wealth-1',
    text: 'I am one with the infinite riches of my subconscious mind. It is my right to be rich, happy, and successful.',
    theme: 'wealth',
    chapter: 'How to Attract Money',
  },
  {
    id: 'wealth-2',
    text: 'Money flows to me freely, copiously, and endlessly. I am forever conscious of my true worth.',
    theme: 'wealth',
    chapter: 'How to Attract Money',
  },
  {
    id: 'wealth-3',
    text: 'I attract wealth and abundance into my life. My subconscious mind is a storehouse of infinite supply.',
    theme: 'wealth',
  },
  {
    id: 'wealth-4',
    text: 'I am grateful for the abundance that surrounds me. Wealth is a state of mind, and I choose prosperity.',
    theme: 'wealth',
  },
  {
    id: 'wealth-5',
    text: 'Every day, in every way, I am becoming more prosperous. My income constantly increases.',
    theme: 'wealth',
    chapter: 'How to Attract Money',
  },

  // Health & Healing
  {
    id: 'health-1',
    text: 'My body is a temple of the living spirit. Every cell in my body vibrates with energy and health.',
    theme: 'health',
    chapter: 'Mental Healings in Modern Times',
  },
  {
    id: 'health-2',
    text: 'The healing power of my subconscious mind flows through every cell of my body, making me whole.',
    theme: 'health',
  },
  {
    id: 'health-3',
    text: 'I am filled with the healing presence of infinite intelligence. Perfect health is my divine right.',
    theme: 'health',
    chapter: 'Mental Healings in Modern Times',
  },
  {
    id: 'health-4',
    text: 'My subconscious mind knows exactly how to heal my body. I trust in its infinite wisdom.',
    theme: 'health',
  },
  {
    id: 'health-5',
    text: 'I radiate health, energy, and vitality. My body responds to the positive thoughts I feed it.',
    theme: 'health',
  },

  // Success & Achievement
  {
    id: 'success-1',
    text: 'Success is mine. I am born to succeed. The infinite intelligence within me guides me to my true place in life.',
    theme: 'success',
    chapter: 'Your Right to Be Rich',
  },
  {
    id: 'success-2',
    text: 'I am always in my true place, doing the thing I love to do. Success follows me in all my endeavors.',
    theme: 'success',
  },
  {
    id: 'success-3',
    text: 'My subconscious mind reveals to me the perfect plan for my success. I follow divine guidance.',
    theme: 'success',
    chapter: 'How to Get the Results You Want',
  },
  {
    id: 'success-4',
    text: 'I expect the best, and I invariably get the best. My thoughts create my reality.',
    theme: 'success',
  },
  {
    id: 'success-5',
    text: 'Every obstacle is a stepping stone to my success. I transform challenges into opportunities.',
    theme: 'success',
  },

  // Peace & Calm
  {
    id: 'peace-1',
    text: 'I am at peace. The peace of God fills my soul. I am serene, poised, and balanced.',
    theme: 'peace',
    chapter: 'How to Use Your Subconscious Mind for Forgiveness',
  },
  {
    id: 'peace-2',
    text: 'I release all worry and anxiety. Divine peace fills my mind and heart.',
    theme: 'peace',
  },
  {
    id: 'peace-3',
    text: 'I am calm and relaxed in every situation. Inner peace is my natural state of being.',
    theme: 'peace',
    chapter: 'How Your Mind Removes Mental Blocks',
  },
  {
    id: 'peace-4',
    text: 'I let go of all fear and embrace tranquility. My mind is a sanctuary of peace.',
    theme: 'peace',
  },
  {
    id: 'peace-5',
    text: 'With every breath, I release tension and welcome serenity. Peace flows through me.',
    theme: 'peace',
  },

  // Relationships & Love
  {
    id: 'relationships-1',
    text: 'I attract loving, harmonious relationships into my life. I am surrounded by love.',
    theme: 'relationships',
    chapter: 'How to Attract the Ideal Mate',
  },
  {
    id: 'relationships-2',
    text: 'I radiate love, and love returns to me multiplied. My relationships are blessed and harmonious.',
    theme: 'relationships',
  },
  {
    id: 'relationships-3',
    text: 'I see the good in everyone, and everyone sees the good in me. Love is the master key.',
    theme: 'relationships',
    chapter: 'How to Use Your Subconscious Mind for Forgiveness',
  },
  {
    id: 'relationships-4',
    text: 'I forgive everyone and set myself free. Holding onto resentment only hurts me.',
    theme: 'relationships',
  },
  {
    id: 'relationships-5',
    text: 'Divine love flows through me to all people I meet. I attract only those who support my highest good.',
    theme: 'relationships',
  },

  // Sleep & Rest
  {
    id: 'sleep-1',
    text: 'I sleep in peace and wake in joy. My subconscious mind works for me while I sleep.',
    theme: 'sleep',
    chapter: 'How Your Subconscious Mind Works While You Sleep',
  },
  {
    id: 'sleep-2',
    text: 'As I drift off to sleep, I release all worries to my subconscious mind. It knows the answer.',
    theme: 'sleep',
  },
  {
    id: 'sleep-3',
    text: 'I hand over all problems to my subconscious before sleep, trusting in its infinite wisdom.',
    theme: 'sleep',
    chapter: 'How Your Subconscious Mind Works While You Sleep',
  },
  {
    id: 'sleep-4',
    text: 'My sleep is deep, restful, and restorative. I awaken refreshed and ready for a new day.',
    theme: 'sleep',
  },
  {
    id: 'sleep-5',
    text: 'Every night, my subconscious processes solutions to my challenges. I wake with clarity.',
    theme: 'sleep',
  },

  // Confidence & Self-Worth
  {
    id: 'confidence-1',
    text: 'I am confident and self-assured. The infinite power within me makes all things possible.',
    theme: 'confidence',
    chapter: 'How to Stay Young in Spirit Forever',
  },
  {
    id: 'confidence-2',
    text: 'I believe in myself and my abilities. My subconscious mind supports my every positive thought.',
    theme: 'confidence',
  },
  {
    id: 'confidence-3',
    text: 'I am worthy of all the good life has to offer. I accept my blessings with gratitude.',
    theme: 'confidence',
  },
  {
    id: 'confidence-4',
    text: 'I face each day with courage and enthusiasm. Fear has no power over me.',
    theme: 'confidence',
    chapter: 'How to Remove Fear',
  },
  {
    id: 'confidence-5',
    text: 'I am unique, valuable, and irreplaceable. My subconscious mind recognizes my true worth.',
    theme: 'confidence',
  },
];

/**
 * Get a daily affirmation based on the date
 * Uses a deterministic selection so the same date always returns the same affirmation
 */
export function getDailyAffirmation(date: Date = new Date()): SubconsciousAffirmation {
  const dayOfYear = Math.floor(
    (date.getTime() - new Date(date.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24)
  );
  const index = dayOfYear % subconsciousAffirmations.length;
  return subconsciousAffirmations[index];
}

/**
 * Get a random affirmation
 */
export function getRandomAffirmation(): SubconsciousAffirmation {
  const index = Math.floor(Math.random() * subconsciousAffirmations.length);
  return subconsciousAffirmations[index];
}

/**
 * Get affirmations by theme
 */
export function getAffirmationsByTheme(
  theme: SubconsciousAffirmation['theme']
): SubconsciousAffirmation[] {
  return subconsciousAffirmations.filter((a) => a.theme === theme);
}

/**
 * Theme display names and colors
 */
export const affirmationThemes: Record<
  SubconsciousAffirmation['theme'],
  { name: string; color: string; emoji: string }
> = {
  wealth: { name: 'Wealth', color: '#d4af37', emoji: '💰' },
  health: { name: 'Health', color: '#38a169', emoji: '💚' },
  success: { name: 'Success', color: '#2b6cb0', emoji: '⭐' },
  peace: { name: 'Peace', color: '#9f7aea', emoji: '🕊️' },
  relationships: { name: 'Love', color: '#e53e3e', emoji: '💝' },
  sleep: { name: 'Rest', color: '#4a5568', emoji: '🌙' },
  confidence: { name: 'Confidence', color: '#ed8936', emoji: '🦁' },
};
